import {
  type EditorImageDimensionDeps,
  type EditorImageNodeContext,
  type EditorImageNodeData,
  type EditorImageNodeReadDeps,
  getEditorImageNodeData,
  normalizeEditorImageDimensions,
} from '@main/shared/utils/lexical/editor-image';
import {
  clampImageHeight,
  clampImageWidth,
  DEFAULT_IMAGE_DIMENSION_CONSTRAINTS,
  ImageDimensionConstraints,
  normalizeImageDimensions,
} from '@main/shared/utils/lexical/image-dimensions';
import {
  $applyNodeReplacement,
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { lazy, Suspense } from 'react';

import { getEditorImageConfig } from './editor-image-config';
import { IMAGE_NODE_TYPE } from './image-node-helpers';

export {
  type EditorImageDimensionDeps,
  type EditorImageNodeContext,
  type EditorImageNodeData,
  type EditorImageNodeReadDeps,
  getEditorImageNodeData,
  normalizeEditorImageDimensions,
} from '@main/shared/utils/lexical/editor-image';

export { IMAGE_NODE_TYPE, isEditorImageNodeType } from './image-node-helpers';

const ImageComponent = lazy(() => import('./ImageComponent'));

const EDITOR_IMAGE_MIME_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']);

export interface ParseSerializedEditorStateDeps {
  parseEditorState?(value: string): unknown;
}

export interface StringifySerializedEditorStateDeps {
  stringifyEditorState?(value: unknown): string;
}

export interface RewriteEditorImageFileIdsDeps
  extends EditorImageNodeReadDeps,
    ParseSerializedEditorStateDeps,
    StringifySerializedEditorStateDeps {
  fileIdMap: Map<string, string>;
}

export interface NormalizeEditorImageNodesForExportDeps extends EditorImageNodeReadDeps {
  targetNodeType?: string;
}

export interface BuildEditorImageHtmlInput {
  image: EditorImageNodeData;
  url: string;
  file?: { name?: string | null };
}

export interface BuildEditorImageHtmlDeps extends EditorImageDimensionDeps {
  className?: string;
  tagName?: string;
  buildStyle?(image: EditorImageNodeData): string;
  escapeAttribute?(value: string): string;
}

export interface ImagePayload {
  altText: string;
  fileId: string;
  fileName: string;
  mimeType: string;
  width?: number;
  height?: number;
  maxWidth?: number;
  key?: NodeKey;
}

export type SerializedImageNode = Spread<
  {
    altText: string;
    fileId: string;
    fileName: string;
    mimeType: string;
    width?: number;
    height?: number;
    maxWidth?: number;
  },
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<React.ReactNode> {
  __altText: string;
  __fileId: string;
  __fileName: string;
  __mimeType: string;
  __width: number | 'inherit';
  __height: number | 'inherit';
  __maxWidth: number;

  static override getType(): string {
    return IMAGE_NODE_TYPE;
  }

  static override clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__altText,
      node.__fileId,
      node.__fileName,
      node.__mimeType,
      node.__width,
      node.__height,
      node.__maxWidth,
      node.__key,
    );
  }

  static override importJSON(serializedNode: SerializedImageNode): ImageNode {
    const { altText, fileId, fileName, mimeType, width, height, maxWidth } = serializedNode;
    const node = $createImageNode({
      altText: altText ?? '',
      fileId,
      fileName: fileName ?? '',
      mimeType: mimeType ?? '',
      width,
      height,
      maxWidth,
    });
    return node;
  }

  override exportDOM(): DOMExportOutput {
    const element = document.createElement('img');
    element.setAttribute('alt', this.__altText);
    element.setAttribute('data-file-id', this.__fileId);
    element.setAttribute('data-file-name', this.__fileName);
    element.setAttribute('data-mime-type', this.__mimeType);
    if (this.__width !== 'inherit') element.setAttribute('width', this.__width.toString());
    if (this.__height !== 'inherit') element.setAttribute('height', this.__height.toString());
    element.style.maxWidth = '100%';
    element.style.height = 'auto';
    if (this.__width !== 'inherit' && this.__height !== 'inherit') {
      element.style.aspectRatio = `${this.__width} / ${this.__height}`;
    }
    return { element };
  }

  static override importDOM(): DOMConversionMap | null {
    return {
      img: (domNode: HTMLElement) => {
        if (
          !domNode.hasAttribute('data-file-id') ||
          !domNode.hasAttribute('data-file-name') ||
          !domNode.hasAttribute('data-mime-type')
        ) {
          return null;
        }

        return {
          conversion: convertImageElement,
          priority: 4,
        };
      },
    };
  }

  constructor(
    altText: string,
    fileId: string,
    fileName: string,
    mimeType: string,
    width?: number | 'inherit',
    height?: number | 'inherit',
    maxWidth?: number,
    key?: NodeKey,
  ) {
    super(key);
    this.__altText = altText;
    this.__fileId = fileId;
    this.__fileName = fileName;
    this.__mimeType = mimeType;
    const normalizedDimensions = normalizeImageDimensions(width, height);
    this.__width = normalizedDimensions.width ?? 'inherit';
    this.__height = normalizedDimensions.height ?? 'inherit';
    this.__maxWidth = maxWidth || DEFAULT_IMAGE_DIMENSION_CONSTRAINTS.maxWidth;
  }

  override exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      fileId: this.getFileId(),
      fileName: this.getFileName(),
      mimeType: this.getMimeType(),
      ...(this.__width === 'inherit' ? {} : { width: this.__width }),
      ...(this.__height === 'inherit' ? {} : { height: this.__height }),
      maxWidth: this.__maxWidth,
      type: IMAGE_NODE_TYPE,
      version: 1,
    };
  }

  setWidthAndHeight(
    width: 'inherit' | number,
    height: 'inherit' | number,
    constraints?: ImageDimensionConstraints,
  ): void {
    const writable = this.getWritable();
    const normalizedDimensions = normalizeImageDimensions(width, height, constraints);
    writable.__width = normalizedDimensions.width ?? 'inherit';
    writable.__height = normalizedDimensions.height ?? 'inherit';
  }

  // Getters
  getAltText(): string {
    return this.__altText;
  }
  getFileId(): string {
    return this.__fileId;
  }
  getFileName(): string {
    return this.__fileName;
  }
  getMimeType(): string {
    return this.__mimeType;
  }
  getWidth(): number | 'inherit' {
    return this.__width;
  }
  getHeight(): number | 'inherit' {
    return this.__height;
  }
  getMaxWidth(): number {
    return this.__maxWidth;
  }

  override createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span');
    const theme = config.theme;
    const className = theme.image;
    if (className !== undefined) {
      span.className = className;
    }
    return span;
  }

  override updateDOM(): false {
    return false;
  }

  override isInline(): boolean {
    return false;
  }

  override decorate(editor: LexicalEditor, config: EditorConfig): React.ReactNode {
    const imageConfig = getEditorImageConfig(editor);

    return (
      <Suspense fallback={null}>
        <ImageComponent
          altText={this.__altText}
          fileId={this.__fileId}
          fileName={this.__fileName}
          mimeType={this.__mimeType}
          width={this.__width}
          height={this.__height}
          maxWidth={this.__maxWidth}
          nodeKey={this.getKey()}
          resizable={true}
          imageConfig={imageConfig}
        />
      </Suspense>
    );
  }
}

export function $createImageNode({
  altText,
  fileId,
  fileName,
  mimeType,
  width,
  height,
  maxWidth,
  key,
}: ImagePayload): ImageNode {
  return $applyNodeReplacement(
    new ImageNode(altText, fileId, fileName, mimeType, width, height, maxWidth, key),
  );
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}

function convertImageElement(domNode: HTMLElement): DOMConversionOutput | null {
  const fileId = domNode.getAttribute('data-file-id')?.trim() ?? '';
  const fileName = domNode.getAttribute('data-file-name')?.trim() ?? '';
  const mimeType = domNode.getAttribute('data-mime-type')?.trim() ?? '';

  if (!fileId || !fileName || !EDITOR_IMAGE_MIME_TYPES.has(mimeType)) {
    return null;
  }

  return {
    node: $createImageNode({
      altText: domNode.getAttribute('alt') ?? '',
      fileId,
      fileName,
      mimeType,
      width: getPositiveNumberAttribute(domNode, 'width'),
      height: getPositiveNumberAttribute(domNode, 'height'),
    }),
  };
}

function getPositiveNumberAttribute(domNode: HTMLElement, name: string) {
  const value = domNode.getAttribute(name);
  if (!value) {
    return undefined;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue > 0 ? Math.round(parsedValue) : undefined;
}

export function parseSerializedEditorState(
  value: string | null | undefined,
  { parseEditorState = parseJsonEditorState }: ParseSerializedEditorStateDeps = {},
) {
  if (!value) {
    return null;
  }

  return parseEditorState(value);
}

export function collectEditorNodeRecords(
  value: unknown,
  {
    visitEditorNodes = defaultVisitEditorNodes,
  }: Pick<EditorImageNodeReadDeps, 'visitEditorNodes'> = {},
) {
  const nodes: Record<string, unknown>[] = [];
  visitEditorNodes(value, (node) => nodes.push(node));
  return nodes;
}

export function getEditorImageNodeDataList(
  nodes: Record<string, unknown>[],
  deps: EditorImageNodeReadDeps = {},
) {
  const getImageNodeData = deps.getImageNodeData ?? ((node) => getEditorImageNodeData(node, deps));
  return nodes.map(getImageNodeData).filter(isEditorImageNodeContext);
}

export function getUniqueEditorImageFileIds(images: EditorImageNodeContext[]) {
  return [...new Set(images.map((image) => image.fileId))];
}

export function collectEditorImageNodes(value: unknown, deps: EditorImageNodeReadDeps = {}) {
  return getEditorImageNodeDataList(collectEditorNodeRecords(value, deps), deps);
}

export function extractEditorImageFileIds(
  value: string | null | undefined,
  deps: EditorImageNodeReadDeps & ParseSerializedEditorStateDeps = {},
) {
  return getUniqueEditorImageFileIds(
    collectEditorImageNodes(parseSerializedEditorState(value, deps), deps),
  );
}

export function rewriteEditorImageFileIds(
  value: string | null | undefined,
  deps: RewriteEditorImageFileIdsDeps,
): string | null | undefined {
  if (!value || deps.fileIdMap.size === 0) {
    return value;
  }

  const editorState = parseSerializedEditorState(value, deps);
  if (!editorState) {
    return value;
  }

  return rewriteParsedEditorImageFileIds(editorState, deps);
}

export function rewriteEditorImageFileIdsInValue(
  value: unknown,
  deps: RewriteEditorImageFileIdsDeps,
): unknown {
  if (Array.isArray(value)) {
    return value.map((child) => rewriteEditorImageFileIdsInValue(child, deps));
  }

  if (!isRecord(value)) {
    return value;
  }

  const rewritten: Record<string, unknown> = {};
  for (const [key, childValue] of Object.entries(value)) {
    rewritten[key] = rewriteEditorImageFileIdsInValue(childValue, deps);
  }

  const getImageNodeData = deps.getImageNodeData ?? ((node) => getEditorImageNodeData(node, deps));
  const image = getImageNodeData(value);
  const newFileId = image && deps.fileIdMap.get(image.fileId);
  if (newFileId) {
    if (typeof rewritten['fileId'] === 'string') {
      rewritten['fileId'] = newFileId;
    }
    if (typeof rewritten['file_id'] === 'string') {
      rewritten['file_id'] = newFileId;
    }
  }

  return rewritten;
}

export function stringifySerializedEditorState(
  value: unknown,
  { stringifyEditorState = JSON.stringify }: StringifySerializedEditorStateDeps = {},
) {
  return stringifyEditorState(value);
}

export function rewriteParsedEditorImageFileIds(
  value: unknown,
  deps: RewriteEditorImageFileIdsDeps,
) {
  return stringifySerializedEditorState(rewriteEditorImageFileIdsInValue(value, deps), deps);
}

export function normalizeEditorImageNodesForExport(
  value: unknown,
  deps: NormalizeEditorImageNodesForExportDeps = {},
): unknown {
  return normalizeEditorImageNodesForExportInValue(value, deps);
}

export function normalizeEditorImageNodesForExportInValue(
  value: unknown,
  deps: NormalizeEditorImageNodesForExportDeps = {},
): unknown {
  if (Array.isArray(value)) {
    return value.map((child) => normalizeEditorImageNodesForExportInValue(child, deps));
  }

  if (!isRecord(value)) {
    return value;
  }

  const normalized: Record<string, unknown> = {};
  for (const [key, childValue] of Object.entries(value)) {
    normalized[key] = normalizeEditorImageNodesForExportInValue(childValue, deps);
  }

  const getImageNodeData = deps.getImageNodeData ?? ((node) => getEditorImageNodeData(node, deps));
  const image = getImageNodeData(value);
  if (image) {
    normalized['type'] = deps.targetNodeType ?? IMAGE_NODE_TYPE;
    normalized['fileId'] = image.fileId;
    normalized['altText'] = image.altText;
    normalized['fileName'] = image.fileName;
    const dimensions = normalizeEditorImageDimensions(
      { width: image.width, height: image.height },
      deps,
    );
    normalized['width'] = dimensions.width;
    normalized['height'] = dimensions.height;
  }

  return normalized;
}

export function clampEditorImageWidth(
  width: number | undefined,
  deps: EditorImageDimensionDeps = {},
) {
  return clampImageWidth(width, deps.dimensionConstraints);
}

export function clampEditorImageHeight(
  height: number | undefined,
  deps: EditorImageDimensionDeps = {},
) {
  return clampImageHeight(height, deps.dimensionConstraints);
}

export function buildEditorImageHtml(
  input: BuildEditorImageHtmlInput,
  deps: BuildEditorImageHtmlDeps = {},
) {
  return renderEditorImageElementHtml(buildEditorImageHtmlAttributes(input, deps), deps);
}

export function buildEditorImageHtmlAttributes(
  { image, url, file }: BuildEditorImageHtmlInput,
  deps: BuildEditorImageHtmlDeps = {},
) {
  const buildStyle = deps.buildStyle ?? ((targetImage) => buildEditorImageStyle(targetImage, deps));
  return [
    ['src', url],
    ['alt', image.altText || image.fileName || file?.name || ''],
    ['class', deps.className ?? 'WysiwygEditorTheme__image'],
    ['style', buildStyle(image)],
    ['loading', 'eager'],
    ['decoding', 'sync'],
  ] as const;
}

export function renderEditorImageElementHtml(
  attributes: readonly (readonly [string, string])[],
  { tagName = 'img', escapeAttribute = escapeHtmlAttribute }: BuildEditorImageHtmlDeps = {},
) {
  return `<${tagName} ${attributes
    .map(([key, value]) => `${key}="${escapeAttribute(value)}"`)
    .join(' ')}>`;
}

export function buildEditorImageStyle(
  image: EditorImageNodeData,
  deps: EditorImageDimensionDeps = {},
) {
  const maxHeight =
    deps.dimensionConstraints?.maxHeight ?? DEFAULT_IMAGE_DIMENSION_CONSTRAINTS.maxHeight;
  const styles = ['max-width: 100%', 'height: auto'];

  const width = clampEditorImageWidth(image.width, deps);
  if (width) {
    styles.push(`width: ${width}px`);
  }

  const height = clampEditorImageHeight(image.height, deps);
  if (height) {
    styles.push(`max-height: ${height}px`);
  } else {
    styles.push(`max-height: ${maxHeight}px`);
  }

  return styles.join('; ');
}

function defaultVisitEditorNodes(value: unknown, visitor: (node: Record<string, unknown>) => void) {
  if (Array.isArray(value)) {
    value.forEach((child) => defaultVisitEditorNodes(child, visitor));
    return;
  }

  if (!isRecord(value)) {
    return;
  }

  visitor(value);
  Object.values(value).forEach((child) => defaultVisitEditorNodes(child, visitor));
}

function parseJsonEditorState(value: string): unknown {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

function isEditorImageNodeContext(
  value: EditorImageNodeContext | undefined,
): value is EditorImageNodeContext {
  return value !== undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function escapeHtmlAttribute(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
